import React from "react";
import { Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./LiveMultiTournamentsDialog.scss";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import MegaphoneIcon from "../../icons/MegaphoneIcon";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import TimeCircleIcon from "../../icons/TimeCircleIcon";
import DotIcon from "../../icons/DotIcon";
import HyperCoin from "../../HyperCoin/HyperCoin";
import { Tournament } from "@nestcoinco/hyper-api-gateway-api-client";
import useCountdown from "../../../hooks/useCountdown";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

interface LiveMultiTournamentsDialogProps extends GenericModalProps {
  tournaments: Tournament[];
  handlePlayTournament: () => void;
}

const renderFiatAmount = (fiatValue: string) => {
  return (
    <Stack direction="horizontal" style={{ gap: 2 }}>
      <span>{fiatValue.charAt(0)}</span>
      <span>{fiatValue.slice(1)}</span>
    </Stack>
  );
};

function LiveMultiTournamentDetails({
  tournamentDetails,
  handleJoinTournament,
}: {
  tournamentDetails: Tournament;
  handleJoinTournament: () => void;
}) {
  const { t } = useTranslation("games");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const timeLeft = useCountdown({
    futureDateOrTotalTimeInMs: tournamentDetails.endDate ?? "",
    showDays: true,
  });

  return (
    <div className="d-flex justify-content-between align-items-center LiveMultiTournamentDetails">
      <Stack direction="horizontal" gap={2} className="align-items-start">
        <Image
          src={tournamentDetails.game?.thumbnail}
          alt={`${tournamentDetails.game?.name} thumbnail`}
          width={53}
          height={53}
          className="GameThumbnail"
        />
        <Stack gap={1}>
          <p className="Grotesk GameTitle">{tournamentDetails.game?.name}</p>
          <Stack direction="horizontal" gap={2}>
            <Stack direction="horizontal" style={{ gap: 2 }}>
              <p className="d-inline-flex align-items-center justify-content-center Grotesk SpecialFontSettings Reward">
                {!isFiatDisplay && <HyperCoin width={10} height={10} />}
                {isFiatDisplay
                  ? renderFiatAmount(
                      formatHprToFiatValue(
                        tournamentDetails.minimumRewardAmount
                      )
                    )
                  : tournamentDetails.minimumRewardAmount}
              </p>
              <span>-</span>
              <p className="d-inline-flex align-items-center justify-content-center Grotesk SpecialFontSettings Reward">
                {!isFiatDisplay && <HyperCoin width={10} height={10} />}
                {isFiatDisplay
                  ? renderFiatAmount(
                      formatHprToFiatValue(tournamentDetails.rewardAmount)
                    )
                  : tournamentDetails.rewardAmount}
              </p>
            </Stack>
            <DotIcon fill="#F0F0F0" width={8} height={8} />
            <Stack direction="horizontal" gap={1} className="Countdown">
              <TimeCircleIcon
                fill="#6C53E8"
                width={10}
                height={10}
                className="Icon"
              />
              <p className="Grotesk Time">{timeLeft}</p>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <PrimaryActionButton
        text={t("join")}
        buttonClassName="d-inline-flex JoinCta"
        onClick={handleJoinTournament}
      />
    </div>
  );
}

export default function LiveMultiTournamentsDialog({
  isOpen,
  onClose,
  tournaments,
  handlePlayTournament,
}: LiveMultiTournamentsDialogProps) {
  const { t } = useTranslation("games");
  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClass="LiveMultiTournaments"
    >
      <Stack gap={4} className="pt-2">
        <Stack gap={3} className="align-items-center">
          <MegaphoneIcon fill="#6C53E8" width={59} height={59} />
          <h2 className="text-center Hanson-700 Heading">
            {t("live_tournaments")}
          </h2>
        </Stack>
        <Stack gap={4}>
          {tournaments.map((tournament) => (
            <LiveMultiTournamentDetails
              key={tournament.id}
              tournamentDetails={tournament}
              handleJoinTournament={handlePlayTournament}
            />
          ))}
        </Stack>
      </Stack>
    </BottomDrawer>
  );
}
